import React from 'react'

type Option = { value: any; label: string }
type GroupedOption = { label: string; options: Option[] }

type FilterConfig = {
  key: string
  label: string
  options?: (Option | GroupedOption)[]
  transform?: (value: any) => string
}

type DateRangeFilter = {
  keyFrom: string
  keyTo: string
  label: string
}

interface Props {
  filters: Record<string, any>
  config: FilterConfig[]
  dateRangeFilters?: DateRangeFilter[]
  formatDate?: (date: string) => string
  clearFilters?: () => void
  onRemoveFilter?: (key: string | string[]) => void
}

export const AppliedFiltersList: React.FC<Props> = ({
  filters,
  config,
  dateRangeFilters = [],
  formatDate = (date) => date,
  clearFilters,
  onRemoveFilter
}) => {
  const appliedFilters = config
    .map(({ key, label, options, transform }) => {
      const value = filters[key]
      // Para valores booleanos, null y undefined son diferentes de false
      if (value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
        return null
      }

      let displayValue: any = value

      // 🔹 Aplanamos opciones si hay grupos (optgroup-like)
      let flattenedOptions: Option[] = []
      if (options) {
        options.forEach((opt) => {
          if ('options' in opt) {
            flattenedOptions.push(...opt.options)
          } else {
            flattenedOptions.push(opt)
          }
        })
      }

      if (Array.isArray(value)) {
        if (options) {
          const labels = value
            .map((v) =>
              flattenedOptions.find((opt) => String(opt.value) === String(v))?.label
            )
            .filter(Boolean)
          displayValue = labels.length ? labels.join(', ') : value.join(', ')
        } else if (transform) {
          displayValue = value.map((v) => transform(v)).join(', ')
        } else {
          displayValue = value.join(', ')
        }
      } else if (options) {
        // Para valores booleanos, comparar con conversión a string
        const valueStr = typeof value === 'boolean' ? String(value) : String(value)
        const match = flattenedOptions.find((opt) => String(opt.value) === valueStr)
        displayValue = match ? match.label : (typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value)
      } else if (transform) {
        displayValue = transform(value)
      } else if (typeof value === 'boolean') {
        displayValue = value ? 'Yes' : 'No'
      }

      return (
        <li key={key} className='filter-badge'>
          <span className='filter-content'>
            <strong>{label}:</strong> {displayValue}
          </span>
          {onRemoveFilter && (
            <button
              className='btn-remove-filter'
              onClick={() => onRemoveFilter(key)}
              title={`Remove ${label} filter`}
              aria-label={`Remove ${label} filter`}
            >
              ×
            </button>
          )}
        </li>
      )
    })
    .filter(Boolean)

  // Procesar rangos de fechas
  const dateRangeFilterElements = dateRangeFilters
    .map(({ keyFrom, keyTo, label }) => {
      const fromValue = filters[keyFrom]
      const toValue = filters[keyTo]

      // Solo mostrar si al menos uno de los valores existe
      if (!fromValue && !toValue) return null

      let displayValue = ''
      if (fromValue && toValue) {
        displayValue = `${formatDate(fromValue)} - ${formatDate(toValue)}`
      } else if (fromValue) {
        displayValue = `From ${formatDate(fromValue)}`
      } else if (toValue) {
        displayValue = `Until ${formatDate(toValue)}`
      }

      return (
        <li key={`${keyFrom}-${keyTo}`} className='filter-badge'>
          <span className='filter-content'>
            <strong>{label}:</strong> {displayValue}
          </span>
          {onRemoveFilter && (
            <button
              className='btn-remove-filter'
              onClick={() => {
                // Remover ambos valores del rango pasando un array
                onRemoveFilter([keyFrom, keyTo])
              }}
              title={`Remove ${label} filter`}
              aria-label={`Remove ${label} filter`}
            >
              ×
            </button>
          )}
        </li>
      )
    })
    .filter(Boolean)

  const allFilters = [...appliedFilters, ...dateRangeFilterElements]

  if (!allFilters.length) return null

  return (
    <div className='filters-applied-container'>
      <button className='btn btn-sm btn-outline-secondary btn-clear-filters' onClick={clearFilters}>
        Clean all filters
      </button>
      <ul className='filters-applied'>{allFilters}</ul>
    </div>
  )
}
